import { Injectable } from '@angular/core';
import { compareSemVersionsWithOperator } from '@kempowersw/csms-utils';
import { GetChargingStationResponse } from '@kempoweroyj/ngx-charging-stations-api-client';
import { HardwareProfile, HwprofilesApiService } from '@kempoweroyj/ngx-hwprofiles-api-client';
import { Observable, Subject, distinctUntilChanged, firstValueFrom, map, of, switchMap, take } from 'rxjs';
import { isEqual as _isEqual } from 'lodash';
import { StationsService } from './stations.service';
import { AuthService } from 'providers/auth.service';

export type StationCapability =
    /** Supports Remote Firmware Updates via ChargEye */
    | 'FirmwareUpdate'
    /** Supports imposing Smart Charging limits */
    | 'SmartCharging'
    /** Supports requesting a list of Smart Charging Profiles, e.g. OCPP2 GetChargingProfiles command? */
    | 'SmartChargingGetProfiles'
    /** Supports requesting OCPP GetCompositeSchedule command? */
    | 'SmartChargingGetSchedule'
    /** Supports requesting a Reset/Restart */
    | 'Restart'
    /** Supports Changing Availability of a Connector/EVSE */
    | 'ChangeAvailability'
    /** Supports viewing diagnostic logs */
    | 'ViewDiagnosticLogs'
    /** Transaction State History view is useful for this charger */
    | 'ViewTransactionStateHistory'
    /** Charger-tile Expand (Charger Hardware dialog) is useful for this charger */
    | 'ViewChargerHardware'
    /** Connectivity-tile Expand (Connectivity dialog) is useful for this charger */
    | 'ViewConnectivityDetails'
    /** Warranty details can be shown for this charger */
    | 'ViewWarrantyInfo'
    /** The charger can be "Pinged" via ChargEye */
    | 'Ping'
    /** ChargEye supports configuring this charger's authorization / free mode setting via distinct UI */
    | 'SettingsAuthorizationConfiguration'
    /** Charger supports Authorization, RFID tag management */
    | 'SettingsAuthorizationTokens'
    /** ChargEye supports setting pricing information to this charger */
    | 'SettingsPricing'
    /** ChargEye supports setting maximum Power to this charger */
    | 'SettingsPower'
    /** ChargEye supports setting remotely the OCPP endpoint this charger should connect to */
    | 'SettingsOcppEndpoint'
    /** ChargEye supports configuring all setting keys via Advanced settings view on this charger */
    | 'SettingsAdvanced'
    /** ChargEye supports configuring standard OCPP setting keys */
    | 'SettingsAdvancedStandard'
    /** ChargEye supports Kempower-specific list of configurations for this charger */
    | 'SettingsAdvancedKempower'
    /** Charger supports Screen image capture / Screenshot of the charger display */
    | 'Screenshot'
    /** Charger supports Kempower Maintenance Tool Offline Access Keys */
    | 'DownloadOfflineAccessKey'
    /** Charger supports the "Change Panel Version" function as found in Kempower chargers */
    | 'ChangePanelVersion'
    /** ChargEye supports Display Customization as supported by Kempower chargers */
    | 'DisplayCustomizationKempower'
    /** ChargEye supports using Shared Settings Profiles with this charging station */
    | 'SharedSettingsProfiles'
    /** ChargEye supports configuring Networking related settings in the charger via specific UI */
    | 'SettingsNetwork'
    /** ChargEye supports viewing/configuring Kempower-specific hardware configuration (e.g. system structure or SystemMetrics) on this charger */
    | 'HardwareKempower'
    /** Charger supports (Eichrect compliant) Signed Meter values */
    | 'SignedMeterData'
    /** ChargEye has access to the native (OCPP websocket) connection details to the charger */
    | 'ViewConnections'
    /** View network connection details */
    | 'ViewNetwork'
    /** ChargEye and Charging station are capable, and makes sense for them, to communicate via OCPP1.6J together. */
    | 'SetupOcpp16jConnectionDetails'
    /** ChargEye offers a function to set hardware profile to this charging station. Not available for Kempower HW or OCPI-integrated chargers. */
    | 'SetHardwareProfile'
    /** Allow requesting JWT Device Auth Token for OCPP2 Kempower chargers to configure it connect to ChargEye */
    | 'SetupOcpp2KempowerConnectionDetails'
    /** Charger supports required smart charging functionalities for Chargeye Power Groups */
    | 'PowerGroups';

export type StationCapabilityCategory = 'NOT_FOUND' | 'UNKNOWN' | 'KEMPOWER_NATIVE' | 'OCPI_INTEGRATED' | 'OCPP_NATIVE';

@Injectable({
    providedIn: 'root',
})
export class StationCapabilitiesService {
    constructor(
        private stationsService: StationsService,
        private authService: AuthService,
        private hwProfilesApi: HwprofilesApiService
    ) {}

    private _cacheCleared$ = new Subject<void>();

    private _isSystemAdmin$ = this.authService.profile$.pipe(
        distinctUntilChanged((a, b) => _isEqual(a?.systemRoles, b?.systemRoles)),
        switchMap(() => {
            const roles = this.authService.getPermissions();

            return of(roles.isSystemAdmin);
        })
    );

    /** Emitted if cache has been cleared */
    cacheCleared$ = this._cacheCleared$.asObservable();

    /** Clear charging station hardware profile cache. Should be called on changes to hardware profiles. */
    clearCache() {
        this._stationHardwareProfileCache.clear();
        this._cacheCleared$.next();
    }

    private _stationHardwareProfileCache = new Map<string, Promise<HardwareProfile>>();

    /** Get (cached) charging station's hardware profile */
    async getStationHardwareProfile(stationId: string): Promise<HardwareProfile> {
        if (this._stationHardwareProfileCache.has(stationId)) {
            return this._stationHardwareProfileCache.get(stationId);
        }
        const profilePromise = this.hwProfilesApi.getStationHardwareProfile(stationId).then((r) => r.hardwareProfile);
        this._stationHardwareProfileCache.set(stationId, profilePromise);

        return profilePromise;
    }

    /**
     * Checks if a charging station supports a certain ChargEye feature or function.
     *
     * Performs Firmware version checks, OCPP connectivity checks and Hardware features in its determination.
     *
     * Example:
     * ```ts
     * const supportsSmartCharging: boolean = await this.stationCapabilitiesService.isCapableOf(stationId, 'SmartCharging');
     * ```
     *
     * @param stationId Charging Station ID CS...
     * @param capability Capability to check for
     * @returns Promise that resolves to `true` if the station is capable of the given feature, `false` otherwise.
     */
    async isCapableOf(stationId: string, capability: StationCapability): Promise<boolean> {
        const stationCapabilities = await this.getCapabilities(stationId);

        return stationCapabilities.includes(capability);
    }

    getStationCapabilityCategory(
        stationId: string
    ): Promise<{ category: StationCapabilityCategory; station?: GetChargingStationResponse }> {
        return firstValueFrom(this.getStationCapabilityCategory$(stationId).pipe(take(1)));
    }

    /**
     *
     * @param stationId Charging Station ID CS...
     * @returns Type/Category of the charger
     */
    getStationCapabilityCategory$(
        stationId: string
    ): Observable<{ category: StationCapabilityCategory; station?: GetChargingStationResponse }> {
        const station$ = this.stationsService.station$(stationId);

        return station$.pipe(
            map((station) => {
                if (!station) {
                    // station not found
                    return { station, category: 'NOT_FOUND' as StationCapabilityCategory };
                }

                // for new charging stations that are not yet connected, we can't determine capabilities, so we assume the things needed for setting up the connection
                if (!station.connection && !station.station.vendorName) {
                    return { station, category: 'UNKNOWN' as StationCapabilityCategory };
                }

                // all actions supported for Kempower Charging station's ChargEye OCPP2 connections
                const isKempowerOcpp2 = station.connection
                    ? station.connection.version === 'ocpp2-kempower'
                    : station.metrics?.length > 0; // use a heuristic to determine if an offline charger is a Kempower - only "native Kempower with OCPP2" have Metrics
                if (isKempowerOcpp2) {
                    return { station, category: 'KEMPOWER_NATIVE' as StationCapabilityCategory };
                }

                // check for OCPI-integrated chargers - i.e. those that are connected to some other backend, and then replicated to ChargEye via OCPI
                const isOCPI = station.connection?.type === 'ocpi';
                if (isOCPI) {
                    return { station, category: 'OCPI_INTEGRATED' as StationCapabilityCategory };
                }

                // some other OCPP integrated charger
                return { station: station as any, category: 'OCPP_NATIVE' as StationCapabilityCategory };
            }),
            distinctUntilChanged((a, b) => a.category === b.category)
        );
    }

    /**
     * Gets a list of capabilities that a charging station supports with a certain ChargEye feature or function.
     *
     * @param stationId Charging Station ID CS...
     * @returns Promise that resolves to list of capabilities that the station supports.
     */
    async getCapabilities(stationId: string): Promise<StationCapability[]> {
        const category = await this.getStationCapabilityCategory(stationId);
        if (category.category === 'NOT_FOUND') {
            // station not found
            return [];
        }

        // for new charging stations that are not yet connected, we can't determine capabilities, so we assume the things needed for setting up the connection
        if (category.category === 'UNKNOWN') {
            const supportedCapabilities = this.getCapabilitiesForNewStation();
            return supportedCapabilities;
        }

        // all actions supported for Kempower Charging station's ChargEye OCPP2 connections
        if (category.category === 'KEMPOWER_NATIVE') {
            const supportedCapabilities = this.getCapabilitiesForKempowerChargingStation(category.station);
            return supportedCapabilities;
        }

        // check for OCPI-integrated chargers - i.e. those that are connected to some other backend, and then replicated to ChargEye via OCPI
        if (category.category === 'OCPI_INTEGRATED') {
            // only a few actions supported for OCPI connections
            const supportedCapabilities = this.getCapabilitiesForOcpi();
            return supportedCapabilities;
        }

        // check hardware profile for other cases (e.g. OCPP1.6J)
        const hardwareProfile = await this.getStationHardwareProfile(stationId);
        let stationCapabilities = this.getCapabilitiesFromHardwareProfile(category.station, hardwareProfile);

        // Add capabilities for system admin
        const isSystemAdmin = await firstValueFrom(this._isSystemAdmin$.pipe(take(1)));

        if (isSystemAdmin) {
            // create set
            const capabilitiesSet = new Set(stationCapabilities);

            // add
            capabilitiesSet.add('ViewNetwork');
            capabilitiesSet.add('SetupOcpp2KempowerConnectionDetails');

            // back to array
            stationCapabilities = Array.from(capabilitiesSet);
        }

        return stationCapabilities;
    }

    /** Capabilities for charging stations integrated to ChargEye via OCPI from other systems */
    private getCapabilitiesForOcpi() {
        const supportedCapabilities: StationCapability[] = ['SmartCharging', 'SettingsAuthorizationTokens'];

        return supportedCapabilities;
    }

    private getCapabilitiesForNewStation(): StationCapability[] {
        const supportedCapabilities: StationCapability[] = [
            'SetupOcpp16jConnectionDetails',
            'ViewConnectivityDetails',
            'ViewNetwork',
            'ViewDiagnosticLogs',
            'ViewConnections',
            'ViewChargerHardware',
            'SetHardwareProfile',
            'SetupOcpp2KempowerConnectionDetails',
        ];

        return supportedCapabilities;
    }

    /** Capabilities of Kempower Charging Stations, based on FW version */
    private getCapabilitiesForKempowerChargingStation(station: GetChargingStationResponse): StationCapability[] {
        const capabilities: StationCapability[] = [
            'FirmwareUpdate',
            'Restart',
            'ChangeAvailability',
            'ViewDiagnosticLogs',
            'ViewTransactionStateHistory',
            'ViewChargerHardware',
            'ViewConnectivityDetails',
            'ViewWarrantyInfo',
            'Ping',
            'SettingsAuthorizationConfiguration',
            'SettingsAuthorizationTokens',
            'SettingsPricing',
            'SettingsPower',
            'SettingsOcppEndpoint',
            'SettingsAdvanced',
            'SettingsAdvancedKempower',
            'SharedSettingsProfiles',
            'ChangePanelVersion',
            'HardwareKempower',
            'Screenshot',
            'SignedMeterData',
            'ViewConnections',
            'ViewNetwork',
            'SetupOcpp2KempowerConnectionDetails',
            'PowerGroups',
        ];

        // Power limitation / Smart Charging capability since Kempower FW 0.15.85
        if (
            compareSemVersionsWithOperator({
                currentFw: station?.station.firmwareVersion,
                compareOperator: '>=',
                compareToFw: '0.15.85',
            })
        ) {
            capabilities.push('SmartCharging');
            capabilities.push('SmartChargingGetProfiles');
            capabilities.push('SmartChargingGetSchedule');
        }
        // Download Offline Access Key capability since Kempower FW 1.6.11
        if (
            compareSemVersionsWithOperator({
                currentFw: station?.station.firmwareVersion,
                compareOperator: '>=',
                compareToFw: '1.6.11',
            })
        ) {
            capabilities.push('DownloadOfflineAccessKey');
        }
        // Display Customization is supported since Kempower FW 1.7.86
        if (
            compareSemVersionsWithOperator({
                currentFw: station?.station.firmwareVersion,
                compareOperator: '>=',
                compareToFw: '1.7.86',
            })
        ) {
            capabilities.push('DisplayCustomizationKempower');
        }
        // Network settings is supported since Kempower FW 1.7.148
        if (
            compareSemVersionsWithOperator({
                currentFw: station?.station.firmwareVersion,
                compareOperator: '>=',
                compareToFw: '1.7.148',
            })
        ) {
            capabilities.push('SettingsNetwork');
        }

        return capabilities;
    }

    /** Capabilities based on assigned Hardware Profile */
    private getCapabilitiesFromHardwareProfile(
        station: GetChargingStationResponse,
        hardwareProfile: HardwareProfile
    ): StationCapability[] {
        const capabilities: StationCapability[] = [
            'Restart',
            'ChangeAvailability',
            'ViewChargerHardware',
            'ViewConnectivityDetails',
            'SettingsAuthorizationTokens',
            'ViewConnections',
            'SetupOcpp16jConnectionDetails',
            'SetHardwareProfile',
            'SettingsAdvancedStandard',
        ];
        if (hardwareProfile?.features?.firmwareUpdate?.supported) {
            capabilities.push('FirmwareUpdate');
        }
        if (hardwareProfile?.features?.smartCharging?.supported) {
            capabilities.push('SmartCharging');

            if (station?.connection?.version === 'ocpp1.6j') {
                // supported in OCPP1.6J
                capabilities.push('SmartChargingGetSchedule');
            }

            if (
                hardwareProfile?.features?.smartCharging?.supportedChargingProfileKind?.supportsChargePointMaxProfileA
            ) {
                capabilities.push('PowerGroups');
            }
        }

        return capabilities;
    }
}
